import type { ReactElement, ReactNode } from 'react'
import { SvgIcon, Typography } from '@mui/material'
import GitHubIcon from '@mui/icons-material/GitHub'
import Link from 'next/link'
import { useRouter } from 'next/router'
import css from './styles.module.css'
import { AppRoutes } from '@/config/routes'
import packageJson from '../../../../package.json'
import ExternalLink from '../ExternalLink'
import MUILink from '@mui/material/Link'
// import { useIsOfficialHost } from '@/hooks/useIsOfficialHost'
// import { HELP_CENTER_URL } from '@safe-global/utils/config/constants'
import { IS_PRODUCTION, COMMIT_HASH } from '@/config/constants'
import type { FooterProps } from './footer.type'
import XIcon from '@mui/icons-material/X'
import LanguageIcon from '@mui/icons-material/Language'
import FileOpenIcon from '@mui/icons-material/FileOpen'
import DiscordIcon from '@/public/images/common/discord-icon-new.svg'

const footerPages = [
  AppRoutes.settings.index,
  AppRoutes.imprint,
  AppRoutes.privacy,
  AppRoutes.cookie,
  AppRoutes.terms,
  AppRoutes.licenses,
]

const FooterLink = ({ children, href }: { children: ReactNode; href: string }): ReactElement => {
  return href ? (
    <Link href={href} passHref legacyBehavior>
      <MUILink>{children}</MUILink>
    </Link>
  ) : (
    <MUILink>{children}</MUILink>
  )
}

const Footer: React.FC<FooterProps> = ({
  forceShow,
  preferences = true,
  versionIcon = true,
  className = css.container,
}): ReactElement | null => {
  const router = useRouter()
  // const isOfficialHost = useIsOfficialHost()
  const initialYear = 2025
  const currentYear = new Date().getFullYear()
  const copyrightYear = initialYear === currentYear ? initialYear : `${initialYear}–${currentYear}`

  if (!footerPages.some((path) => router.pathname.startsWith(path)) && !forceShow) {
    return null
  }

  const getHref = (path: string): string => {
    return router.pathname === path ? '' : path
  }

  return (
    <footer className={className}>
      <ul>
        <li>
          <Typography variant="caption">&copy;{copyrightYear} Zircuit Safe</Typography>
        </li>
        <li>
          <ExternalLink href="https://www.zircuit.com/" noIcon>
            <SvgIcon component={LanguageIcon} inheritViewBox fontSize="inherit" sx={{ mr: 0.5 }} /> Website
          </ExternalLink>
        </li>
        <li>
          <ExternalLink href="https://x.com/ZircuitL2" noIcon>
            <SvgIcon component={XIcon} inheritViewBox fontSize="inherit" sx={{ mr: 0.5 }} /> ZircuitL2
          </ExternalLink>
        </li>
        <li>
          <ExternalLink href="https://discord.com/invite/zircuit" noIcon>
            <SvgIcon component={DiscordIcon} inheritViewBox fontSize="inherit" sx={{ mr: 0.5 }} /> Discord
          </ExternalLink>
        </li>
        <li>
          <ExternalLink href="https://dubiw3zgo51jg.cloudfront.net/tos.pdf" noIcon>
            <SvgIcon component={FileOpenIcon} inheritViewBox fontSize="inherit" sx={{ mr: 0.5 }} /> Terms Of Service
          </ExternalLink>
        </li>
        <li>
          <ExternalLink href="https://dubiw3zgo51jg.cloudfront.net/pp.pdf" noIcon>
            <SvgIcon component={FileOpenIcon} inheritViewBox fontSize="inherit" sx={{ mr: 0.5 }} /> Privacy Policy
          </ExternalLink>
        </li>
        <li>
          <FooterLink href={getHref(AppRoutes.cookie)}>Cookie policy</FooterLink>
        </li>
        {preferences && (
          <li>
            <FooterLink href={getHref(AppRoutes.settings.index)}>Preferences</FooterLink>
          </li>
        )}
        <li>
          <ExternalLink href={`${packageJson.homepage}/releases/tag/v${packageJson.version}`} noIcon>
            {versionIcon && <SvgIcon component={GitHubIcon} inheritViewBox fontSize="inherit" sx={{ mr: 0.5 }} />}v
            {packageJson.version}
          </ExternalLink>
        </li>
        {!IS_PRODUCTION && COMMIT_HASH && (
          <li>
            <ExternalLink href={`${packageJson.homepage}/commit/${COMMIT_HASH}`} noIcon>
              {COMMIT_HASH.slice(0, 7)}
            </ExternalLink>
          </li>
        )}
      </ul>
    </footer>
  )
}

export default Footer
